if (!customElements.get('product-tabs')) {
  class ProductTabs extends HTMLElement {
    constructor() {
      super();
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      // Elements
      this.desktopTabs = this.querySelectorAll('.meta-tab-button');
      this.mobileTriggers = this.querySelectorAll('.meta-mobile-trigger');
      this.panes = this.querySelectorAll('.meta-tab-pane');
      this.specContainer = this.querySelector('[id^="SpecificationTable-"]');

      // Listeners
      this.desktopTabs.forEach((tab) => {
        tab.addEventListener('click', this.onDesktopTabClick.bind(this));
      });

      this.mobileTriggers.forEach((trigger) => {
        trigger.addEventListener('click', this.onMobileAccordionClick.bind(this));
      });

      // Variant Spec Logic
      const dataScript = this.querySelector('[id^="VariantSpecifications-"]');
      if (dataScript) {
        try {
          this.variantData = JSON.parse(dataScript.textContent);
        } catch (e) {
          console.error('Error parsing variant specification data', e);
        }
      }
      document.addEventListener('variant:changed', this.onVariantChange);
      this.syncVisibilityState();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this.onVariantChange);
    }

    /* Desktop: Standard Tab Behavior */
    onDesktopTabClick(event) {
      event.preventDefault();
      this.activateTab(event.currentTarget);
    }

    /* Mobile: Accordion Behavior (Now Toggles) */
    onMobileAccordionClick(event) {
      event.preventDefault();
      const trigger = event.currentTarget;

      // Check if this trigger is already active
      if (trigger.classList.contains('active')) {
        this.closeAll(); // Toggle closed
      } else {
        this.activateTab(trigger); // Open it
      }
    }

    /* Helper: Deactivate all tabs/panes */
    closeAll() {
      this.desktopTabs.forEach(t => {
        t.classList.remove('active');
        t.setAttribute('aria-selected', 'false');
      });
      this.mobileTriggers.forEach(t => {
        t.classList.remove('active');
        t.setAttribute('aria-expanded', 'false');
      });
      this.panes.forEach(p => p.classList.remove('active'));
    }

    getVisibleTabs() {
      return Array.from(this.desktopTabs).filter(tab => !tab.hidden);
    }

    getVisibleMobileTriggers() {
      return Array.from(this.mobileTriggers).filter(trigger => !trigger.hidden);
    }

    getVisiblePanes() {
      return Array.from(this.panes).filter(pane => !pane.hidden);
    }

    activateFirstVisibleTab() {
      const firstVisibleTab = this.getVisibleTabs()[0] || this.getVisibleMobileTriggers()[0];
      if (firstVisibleTab) {
        this.activateTab(firstVisibleTab);
      }
    }

    syncVisibilityState() {
      const hasVisiblePanes = this.getVisiblePanes().length > 0;
      this.hidden = !hasVisiblePanes;

      if (!hasVisiblePanes) {
        this.closeAll();
        return;
      }

      const activePane = this.querySelector('.meta-tab-pane.active');
      if (!activePane || activePane.hidden) {
        this.activateFirstVisibleTab();
      }
    }

    setTabVisibility(targetPane, isVisible) {
      if (!targetPane) return;

      const targetId = targetPane.id;
      const desktopBtn = this.querySelector(`.meta-tab-button[data-tab-target="${targetId}"]`);
      const mobileBtn = this.querySelector(`.meta-mobile-trigger[data-tab-target="${targetId}"]`);
      const controls = [desktopBtn, mobileBtn, targetPane];

      controls.forEach((element) => {
        if (!element) return;

        element.hidden = !isVisible;
        if (isVisible) {
          element.removeAttribute('aria-hidden');
        } else {
          element.classList.remove('active');
          element.setAttribute('aria-hidden', 'true');
        }
      });

      if (desktopBtn && !isVisible) {
        desktopBtn.setAttribute('aria-selected', 'false');
      }

      if (mobileBtn && !isVisible) {
        mobileBtn.setAttribute('aria-expanded', 'false');
      }
    }

    /* Unified Activation Logic */
    activateTab(clickedElement) {
      const targetId = clickedElement.getAttribute('data-tab-target');
      const targetPane = this.querySelector(`#${targetId}`);
      if (clickedElement.hidden || !targetPane || targetPane.hidden) return;

      // Reset All first
      this.closeAll();

      // Activate Desktop Button
      const desktopBtn = this.querySelector(`.meta-tab-button[data-tab-target="${targetId}"]`);
      if(desktopBtn) {
        desktopBtn.classList.add('active');
        desktopBtn.setAttribute('aria-selected', 'true');
      }

      // Activate Mobile Trigger
      const mobileBtn = this.querySelector(`.meta-mobile-trigger[data-tab-target="${targetId}"]`);
      if(mobileBtn) {
        mobileBtn.classList.add('active');
        mobileBtn.setAttribute('aria-expanded', 'true');
      }

      // Activate Pane
      if (targetPane) {
        targetPane.classList.add('active');
      }
    }

    onVariantChange(event) {
      const variant = event.detail.variant;
      if (!variant || !this.variantData || !this.specContainer) return;

      const newHtml = this.variantData[variant.id] || '';
      const hasSpecs = newHtml.trim().length > 0;

      if (hasSpecs) {
        this.specContainer.innerHTML = newHtml;
      } else {
        this.specContainer.innerHTML = '';
      }

      this.setTabVisibility(this.specContainer.closest('.meta-tab-pane'), hasSpecs);
      this.syncVisibilityState();
    }
  }
  customElements.define('product-tabs', ProductTabs);
}
